// Intake wizard (issue #10): photos -> vision review (REQUIRED edit step) ->
// theme + generate. Mirrors kit-engine contracts: extraction shape from
// intake_schema.py, WORD_RE identical to the server-side injection check.
import { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { classifyTheme, createKit, extractWords, getKitOnce, THEME_CATALOG, uploadPhoto, watchKids } from "../api";
import { WORD_RE, type Extraction, type ExtractionSection, type Kid } from "../types";
import { track, EVENTS } from "../analytics";

const STEPS = ["photos", "review", "generate"] as const;

// #32 review fix: pastes arrive comma-separated and numbered ("1. pass").
// kitGenerate's WORD_RE rejects "pass," or "1." — clean tokens at parse time.
export function cleanWord(w: string): string {
  const c = w.replace(/^[^\w'\-]+/, "").replace(/[^\w'\-]+$/, "");
  return /[A-Za-z]/.test(c) ? c : ""; // drop pure numbering tokens ("1", "2")
}

// #32: manual word-list parser — mirrors parseManualWordList in
// apps/mobile/lib/types.dart (keep the two in sync; mobile has unit tests).
export function parseManualWordList(raw: string): ExtractionSection[] {
  const sections: ExtractionSection[] = [];
  let name = "Word List"; let words: string[] = [];
  for (const line of raw.split("\n")) {
    const t = line.trim();
    if (!t) continue;
    if (t.endsWith(":")) {
      if (words.length) { sections.push({ name, words }); words = []; }
      name = t.slice(0, -1).trim();
    } else {
      words.push(...t.split(/[\s,;]+/).map(cleanWord).filter((w) => w.length > 0 && /[A-Za-z]/.test(w)));
    }
  }
  if (words.length) sections.push({ name, words });
  return sections;
}
type Step = (typeof STEPS)[number];

export default function Wizard() {
  const nav = useNavigate();
  const [params] = useSearchParams();
  const copyFrom = params.get("copyFrom"); // "make another copy of this module"
  const [step, setStep] = useState<Step>(copyFrom ? "generate" : "photos");
  const [files, setFiles] = useState<File[]>([]);
  const [ex, setEx] = useState<Extraction | null>(null);
  const [busy, setBusy] = useState(false);
  const [uploadDone, setUploadDone] = useState(0); // #33 stage
  const [extracting, setExtracting] = useState(false); // #33 stage
  const [error, setError] = useState("");
  const [manual, setManual] = useState(false);       // #32 manual entry mode
  const [manualText, setManualText] = useState("");  // #32
  const fileRef = useRef<HTMLInputElement>(null);
  const camRef = useRef<HTMLInputElement>(null); // #32
  const started = useRef(false);
  useEffect(() => {
    if (started.current) return;
    started.current = true;
    track(EVENTS.intakeStart, copyFrom ? { source: "duplicate" } : undefined);
    if (copyFrom) {
      // Duplicate flow: words come from the stored kit (server re-reads the
      // source too — this is prefilled UI). kid/theme/format re-picked below.
      getKitOnce(copyFrom).then((k) => {
        if (!k || !k.extraction) { setError("That kit no longer has a saved word list to copy."); return; }
        setEx(k.extraction);
      }).catch((e) => setError(String((e as { message?: string }).message ?? e)));
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  async function toReview() {
    setBusy(true); setError(""); setUploadDone(0); setExtracting(false);
    try {
      // #33: stage-labeled progress — uploads are the silent multi-second
      // phase before vision even starts.
      const ids = [];
      for (const f of files) {
        ids.push(await uploadPhoto(f));
        setUploadDone(ids.length);
      }
      setExtracting(true);
      const extraction = await extractWords(ids);
      setEx(extraction);
      setStep("review");
      track(EVENTS.intakeExtract, {
        photo_count: ids.length,
        word_count: extraction.sections.reduce((n, s) => n + s.words.length, 0),
        section_count: extraction.sections.length,
      });
    } catch (e) { setError(String((e as { message?: string }).message ?? e)); }
    finally { setBusy(false); setExtracting(false); }
  }

  return (
    <>
      <h1>New weekly kit</h1>
      <div className="steps">
        {STEPS.map((s) => <span key={s} className={`step ${s === step ? "active" : ""}`}>{s}</span>)}
      </div>
      {error && <p className="err">{error}</p>}

      {step === "photos" && (
        <div className="card">
          <p>Upload 1–4 photos or PDFs of this week's word list from school.</p>
          <p className="muted">…or type/paste the list if your school posts it as text.</p>
          <div className="dropzone" onClick={() => fileRef.current?.click()}>
            {files.length === 0 ? "Tap to choose photos or PDFs" : `${files.length} file(s) selected`}
          </div>
          {/* #32: a SEPARATE capture input — capture + multiple together breaks
              gallery picking on iOS mobile web (review fix) */}
          <input ref={fileRef} type="file" accept="image/*,application/pdf" multiple hidden
            onChange={(e) => setFiles(Array.from(e.target.files ?? []).slice(0, 4))} />
          <input ref={camRef} type="file" accept="image/*" capture="environment" hidden
            onChange={(e) => setFiles(Array.from(e.target.files ?? []).slice(0, 4))} />{" "}
          <button className="ghost" onClick={() => camRef.current?.click()}>Take photo</button>
          {/* #32: manual entry — lines ending ':' name a section; skips vision entirely */}
          <button className="ghost" onClick={() => setManual(!manual)}>
            {manual ? "Cancel manual entry" : "Type or paste the list"}
          </button>
          {manual && (
            <p>
              <textarea rows={8} cols={60} placeholder={"Spelling - On Level:\npass mess buzz\n\nSight Words:\nwas said look"}
                value={manualText} onChange={(e) => setManualText(e.target.value)} />
              {" "}<button onClick={() => {
                const sections = parseManualWordList(manualText);
                if (!sections.length) { setError("no words found in your text"); return; }
                setEx({ title: "Weekly Words", sections });
                setStep("review");
                track(EVENTS.intakeExtract, { source: "manual",
                  word_count: sections.reduce((n, s) => n + s.words.length, 0),
                  section_count: sections.length });
              }}>Review words</button>
            </p>
          )}
          {/* #33: stage-labeled busy state instead of a swapped button label */}
          {busy ? (
            <p role="status" aria-live="polite">
              <span className="spinner" aria-hidden="true" />{" "}
              {extracting ? "Reading your word list… (usually under a minute)" : `Uploading ${uploadDone} of ${files.length}…`}
            </p>
          ) : (
            <p><button disabled={files.length === 0} onClick={toReview}>Extract words</button></p>
          )}
          <p className="muted">Photos are processed for the word list, then deleted by default (retention settings in #18).</p>
        </div>
      )}

      {step === "review" && ex && (
        <ReviewStep ex={ex} setEx={setEx} onNext={() => { track(EVENTS.reviewComplete); setStep("generate"); }} />
      )}

      {step === "generate" && !ex && copyFrom && (
        // duplicate mode: source words still loading (parity with mobile spinner)
        <p role="status" aria-live="polite"><span className="spinner" aria-hidden="true" /> Loading the saved word list…</p>
      )}
      {step === "generate" && ex && (
        // #36 parity: replace so browser back from the kit page lands on Home,
        // not on the spent wizard (which invites a duplicate kit).
        <GenerateStep ex={ex} duplicateOf={copyFrom ?? undefined} onDone={(kitId) => nav(`/kit/${kitId}`, { replace: true })} onError={setError} />
      )}
    </>
  );
}

function ReviewStep({ ex, setEx, onNext }: {
  ex: Extraction; setEx: (e: Extraction) => void; onNext: () => void;
}) {
  // The review step is non-negotiable (SPEC: trust, not magic) — parent must
  // click through it; edits here are what the engine consumes.
  function setSection(i: number, sec: ExtractionSection) {
    const sections = ex.sections.slice(); sections[i] = sec; setEx({ ...ex, sections });
  }
  const bad = ex.sections.flatMap((s) => s.words).filter((w) => !WORD_RE.test(w));

  return (
    <div className="card">
      <label>Title <input value={ex.title} onChange={(e) => setEx({ ...ex, title: e.target.value })} /></label>
      {ex.sections.map((sec, i) => (
        <div key={i} style={{ marginTop: 16 }}>
          <h2>{sec.name}</h2>
          <textarea
            rows={2} cols={60}
            value={sec.words.join(" ")}
            onChange={(e) => setSection(i, { ...sec, words: e.target.value.split(/\s+/).filter(Boolean) })}
          />
          <p className="muted">{sec.words.length} words — separate with spaces; fix any red ones.</p>
          <p>{sec.words.map((w, j) => (
            <span key={j} className={`word-chip ${WORD_RE.test(w) ? "" : "bad"}`}>{w}</span>
          ))}</p>
        </div>
      ))}
      <p>
        <button disabled={bad.length > 0} onClick={onNext}>Looks right — continue</button>
        {bad.length > 0 && <span className="err"> {bad.length} word(s) have invalid characters</span>}
      </p>
    </div>
  );
}

function GenerateStep({ ex, duplicateOf, onDone, onError }: {
  ex: Extraction; duplicateOf?: string; onDone: (kitId: string) => void; onError: (e: string) => void;
}) {
  const [kids, setKids] = useState<Kid[]>([]);
  const [kidId, setKidId] = useState("");
  const [theme, setTheme] = useState(THEME_CATALOG[0].slug);
  const [bookletFormat, setBookletFormat] = useState<"saddle" | "flat">("saddle"); // #35
  const [idea, setIdea] = useState("");
  const [redirect, setRedirect] = useState<{ slug: string; msg: string } | null>(null);
  const [busy, setBusy] = useState(false);
  const [paywall, setPaywall] = useState(false);

  useEffect(() => {
    const un = watchKids((ks) => { setKids(ks); if (ks.length && !kidId) setKidId(ks[0].id); });
    return un;
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Duplicate mode: prefill theme + print style from the source kit.
  useEffect(() => {
    if (!duplicateOf) return;
    getKitOnce(duplicateOf).then((k) => {
      if (!k) return;
      if (k.theme && THEME_CATALOG.some((t) => t.slug === k.theme)) setTheme(k.theme);
      if (k.bookletFormat) setBookletFormat(k.bookletFormat);
    });
  }, [duplicateOf]);

  async function checkIdea() {
    if (!idea.trim()) return;
    // #14 block-and-redirect: never lose the kid's joy — offer the catalog.
    const r = await classifyTheme(idea);
    if (r.verdict !== "clean" && r.redirectTheme) {
      const t = THEME_CATALOG.find((x) => x.slug === r.redirectTheme);
      if (t) { setTheme(t.slug); setRedirect({ slug: t.slug, msg: r.message ?? "" }); }
    } else {
      setRedirect(null);
    }
  }

  async function go() {
    if (!kidId) { onError("add a kid first (Home → Add a kid)"); return; }
    setBusy(true);
    try {
      const kitId = await createKit(kidId, ex, theme, bookletFormat, duplicateOf);
      track(EVENTS.kitGenerate, {
        word_count: ex.sections.reduce((n, s) => n + s.words.length, 0),
        theme,
        ...(duplicateOf ? { duplicate_of: duplicateOf } : {}),
      });
      onDone(kitId);
    }
    catch (e) {
      const err = e as { code?: string; message?: string };
      // kitGenerate throws permission-denied when the plan gate closes.
      if (err.code === "permission-denied" && /upgrade|plan/i.test(err.message ?? "")) {
        setPaywall(true);
        track(EVENTS.paywallSeen, { from: "generate" });
      }
      onError(String(err.message ?? e));
    }
    finally { setBusy(false); }
  }

  return (
    <div className="card">
      <p>Generate the kit for <strong>{ex.title}</strong> ({ex.sections.reduce((n, s) => n + s.words.length, 0)} words):
        flashcards, word search + key, crossword + key, handwriting sheet, and a storybook.</p>
      <label>Kid
        <select value={kidId} onChange={(e) => setKidId(e.target.value)}>
          {kids.length === 0 && <option value="">no kids yet — add one on Home</option>}
          {kids.map((k) => <option key={k.id} value={k.id}>{k.name}</option>)}
        </select>
      </label>
      <label>Ideal theme? <input value={idea} onChange={(e) => setIdea(e.target.value)} placeholder="e.g. dinosaurs" onBlur={checkIdea} /></label>
      {redirect && <p className="muted">We kept you original and safe — switched to <strong>{THEME_CATALOG.find(t=>t.slug===redirect.slug)?.name}</strong>. {redirect.msg}</p>}
      <label>Theme
        <select value={theme} onChange={(e) => setTheme(e.target.value)}>
          {THEME_CATALOG.map((t) => <option key={t.slug} value={t.slug}>{t.name} — {t.world}</option>)}
        </select>
      </label>
      {/* #35: print style is a wizard-time choice (single-pass pipeline) */}
      <label>Storybook print style
        <select value={bookletFormat} onChange={(e) => setBookletFormat(e.target.value as "saddle" | "flat")}>
          <option value="saddle">Fold &amp; staple booklet (4 sheets, duplex)</option>
          <option value="flat">Flat pages — 1 per sheet, no folding (16 sheets)</option>
        </select>
      </label>
      <p><button disabled={busy} onClick={go}>{busy ? "Starting…" : "Generate kit"}</button></p>
      {paywall && (
        <div className="card">
          <p><strong>That&apos;s this week&apos;s free kit.</strong> The free plan includes one kit a week — your next free kit comes around next week, or upgrade for a kit for every weekly list, all year.</p>
          <p><Link to="/pricing" onClick={() => track(EVENTS.paywallUpgrade, { from: "generate" })}>See plans</Link></p>
        </div>
      )}
    </div>
  );
}
